import {Component, EventEmitter, Input, OnInit, Output, ViewChild, inject, DestroyRef} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TenantService } from 'src/app/services/api/tenant.service';
import { translate } from '@jsverse/transloco';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { buildPageable } from 'src/app/services/util/pageable-util';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { ToastrService } from 'ngx-toastr';
import { InstancesService } from 'src/app/services/api/instances.service';
import { InstanceEndpointsComponent } from './instance-endpoints/instance-endpoints.component';
import { InstancesDetailsComponent } from './instances-details/instances-details.component';
import { DateTimeFormatService } from 'src/app/services/util/date-time-format.service';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { ColumnFilterComponent } from './column-filter/column-filter.component';
import { forkJoin, Subscription } from 'rxjs';
import { BreadcrumbItem } from '../../models/breadcrumb/breadcrumb-item';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { ManagementTypes } from 'src/app/models/management-type';
import { ToastrErrorService } from 'src/app/services/util/toastr-error.service';
import cloneDeep from 'lodash/cloneDeep';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { Permissions, Datazone, Attributes } from 'src/app/models/principal/permission';
import { PermissionsService } from 'src/app/services/util/permissions.service';

const REQUIRED_PERMISSIONS = [
  {
      key: PermissionKeys.INSTANCE_READ_DATAZONE_NONPII,
      action: Permissions.INSTANCE_READ,
      attribute: Attributes.DATAZONE,
      attributeValue: Datazone.NONPII
    },
    {
      key: PermissionKeys.INSTANCE_READ_DATAZONE_PII,
      action: Permissions.INSTANCE_READ,
      attribute: Attributes.DATAZONE,
      attributeValue: Datazone.PII
    },
    {
      key: PermissionKeys.INSTANCE_READ_DATAZONE_LIVE,
      action: Permissions.INSTANCE_READ,
      attribute: Attributes.DATAZONE,
      attributeValue: Datazone.LIVE
    },
];

@Component({
    selector: 'app-instances-mgmt',
    templateUrl: './instances-mgmt.component.html',
    styleUrls: ['./instances-mgmt.component.scss'],
    standalone: false
})
export class InstancesMgmtComponent  implements OnInit {
  @ViewChild(MatSort, {static: true}) sort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
private destroyRef = inject(DestroyRef);
  DeployStatus = DeployStatus;
  breadcrumbItems: BreadcrumbItem[];
  ManagementTypes = ManagementTypes;

  tenant: string;
  instanceData: any;
  displayedColumns: string[] = ['name', 'tenantId', 'dateCreated', 'regionPair', 'dataZone', 'deploymentClass',
   'randomSuffix', 'status', 'menu'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource([]);
  showSpinner = true;
  searchSolutionText: any;
  dropdownsList: any = [];
  dashboardLink: any;
  instanceLink: any;
  pageSize: number;
  pageNumber = 1;

  paginationSize = 10;
  pageIndex = 0;
  apiPageNumber = 0;
  recordsPerAPI = 100;
  showPagination = true;
  sortInfo: any;
  tenantList: any;
  tenantId: any;
  status: any;
  filteredColumns: any;
  clearData: boolean;
  subscription: Subscription;
  defaultInstances: any;
  searchText: string;
  defaultFilterOptions: any;
  selectedDefaultFilter: any;
  managementType: any;
  isFailed = false;
  statusList: any;
  isEmpty = true;
  isMultipleTenant = false;
  permissionsMap: { [key: string]: boolean };
  totalRecords: number = 0;
  allFetchedInstances: any[] = [];

  constructor(private route: ActivatedRoute, private authService: AuthService, private instanceService: InstancesService,
    private sidenavService: SidenavService, private toastrService: ToastrService, private tenantService: TenantService,
    public dateTimeFormatService: DateTimeFormatService, private solutionsService: SolutionsService,
    private customToastrService: CustomToastrService, private router: Router, private toastrErrorService: ToastrErrorService,
    private permissionsService: PermissionsService) {
    
  }

  ngOnInit(): void {
     this.permissionsMap = this.permissionsService.resolveAPIPermissions(REQUIRED_PERMISSIONS);
     if(!PermissionKeys.INSTANCE_READ_DATAZONE_NONPII || !PermissionKeys.INSTANCE_READ_DATAZONE_PII || !PermissionKeys.INSTANCE_READ_DATAZONE_LIVE) {
      this.router.navigate(['/', this.authService.principal.provider, 'permission-unauthorized'], {skipLocationChange: true});
    }
    this.sidenavService.bookmarkDelete
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (this.instanceData.data.length > 0) {
          this.instanceData.data.find(item => item.id === data.id).bookmarked = false;
          this.dataSource.filteredData.find(item => item.id === data.id).bookmarked = false;
        }
      });
    this.sidenavService.selectedMenu = 'LEFT_SIDE_NAV.INSTANCE';
    this.pageIndex = 0;
    const tenantData = localStorage.getItem('filteredTenant');
     if (tenantData) {
      const parsedTenant = JSON.parse(tenantData); 
      if (Array.isArray(parsedTenant)) {
        this.isMultipleTenant = parsedTenant.length > 1;
        this.tenantId = parsedTenant;
      } else {
        this.isMultipleTenant = false;
        this.tenantId = parsedTenant;
      }
    }
    const statusData = localStorage.getItem('filteredStatus');
    if (statusData) {
      this.status = statusData;
    }
    const typeData = localStorage.getItem('filteredType');
    if (typeData) {
      this.managementType = typeData;
    }

    this.dashboardLink = ({label: translate('LEFT_SIDE_NAV.DASHBOARDS')});
    this.instanceLink = ({label: translate('LEFT_SIDE_NAV.INSTANCE'), url: ['instance']});

    const statusOptions = [];
    const allStatus = Object.values(DeployStatus);
    allStatus.forEach(val => {
      statusOptions.push({value: val, selected: false});
    });

    const typeOptions = [
      {value: ManagementTypes.EXTERNAL, label: translate('INSTANCE_LIST.EXTERNAL'), selected: false},
      {value: ManagementTypes.EXTERNAL_FOR_ACTION, label: translate('INSTANCE_LIST.EXTERNAL_FOR_ACTION'), selected: false}
    ];

    this.dropdownsList = [
      {
        title: 'Tenant',
        placeholder: translate('INSTANCE_LIST.SELECT_TENANT'),
        key: 'tenant',
        options: []
      },
      {
        title: 'Status',
        placeholder: translate('INSTANCE_LIST.SELECT_STATUS'),
        key: 'status',
        options: []
      },
      {
        title: 'Type',
        placeholder: translate('INSTANCE_LIST.SELECT_TYPE'),
        key: 'type',
        options: typeOptions
      }
    ];
    this.defaultFilterOptions = [
      {
        label: translate('INSTANCE_LIST.ALL_INSTANCES'),
        selected: true
      },
      {
        key: DeployStatus.DEPLOYED,
        label: translate('INSTANCE_LIST.DEPLOYED_INSTANCES'),
        selected: false,
      },
      {
        key: DeployStatus.DEPLOYING,
        label: translate('INSTANCE_LIST.DEPLOYING_INSTANCES'),
        selected: false,
      },
      {
        key: DeployStatus.FAILED,
        label: translate('INSTANCE_LIST.FAILED_INSTANCES'),
        selected: false,
      },
    ];
    if (this.status && this.defaultFilterOptions.find(item => item.key === this.status)) {
      this.selectedDefaultFilter = this.defaultFilterOptions.find(item => item.key === this.status);
    } else {
      this.selectedDefaultFilter = this.defaultFilterOptions[0];
    }
    this.statusList = [...new Set(statusOptions.map(item => item.value).sort((a, b) => (a.toLowerCase() < b.toLowerCase()) ? -1 : 1))];
    this.statusList.forEach((value) => {
      this.dropdownsList[1].options.push({value, selected: false});
    });
    this.clearData = true;
    this.tenant = this.authService.principal.provider;
    this.getTenants();
    this.getInstancesData(this.tenantId, this.status, this.managementType, this.searchSolutionText, this.pageIndex, this.paginationSize, true);
  }

  /**
   * Getting a tenant List to be displayed in filter removing all the external tenants.
   */
  getTenants(): void {
    this.dropdownsList[0].options = [];
    this.tenantService.getTenants()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data) {
          data = data.filter(value => value.managementType !== 'external' && value.tenantId !== 'temenos.com');
          this.tenantList = [...new Set(data.map(item => item.tenantId).sort((a, b) => (a.toLowerCase() < b.toLowerCase()) ? -1 : 1))];
          this.tenantList.forEach((value) => {
          this.dropdownsList[0].options.push({value, selected: false});
        });
        }
      });
  }

  /**
   * Get the instance for the selected filter data
   * @param tenantId - the tenant to filter
   * @param status - the status to filter
   * @param managementType - the managementType to filter
   * @param solutionId - the solutionId to filter
   * @param page - the page size to be displayed
   * @param pageNumber - the selected page number
   */
  getInstancesData(tenantId?, status?, managementType?, solutionId?, pageIndex?, paginationSize?, fetchAll: boolean = false): void {
    this.showSpinner = true;
    this.isFailed = false;
    this.instanceData = [];
    this.loadData(this.instanceData);

    let multipleTenant;
     if (tenantId) {
      if (Array.isArray(tenantId)) {
        this.tenantId = tenantId;
        multipleTenant = tenantId;
        this.isMultipleTenant = tenantId.length > 1;
      } else if (typeof tenantId === 'string' && tenantId.includes(',')) {
        this.tenantId = tenantId.split(',').map(t => t.trim());
        multipleTenant = this.tenantId;
        this.isMultipleTenant = true;
      } else {
        this.tenantId = tenantId;
        multipleTenant = [tenantId];
        this.isMultipleTenant = false;
      }
      localStorage.setItem('filteredTenant', JSON.stringify(this.tenantId));
    } else {
      localStorage.removeItem('filteredTenant');
    }
    if(status) {
      localStorage.setItem('filteredStatus', status);
    } else {
      localStorage.removeItem('filteredStatus');
    }

    if(managementType) {
      localStorage.setItem('filteredType', status);
    } else {
      localStorage.removeItem('filteredType');
    }
    if (Array.isArray(multipleTenant) && multipleTenant.length > 1) {
      const requests = multipleTenant.map(tid =>
        this.instanceService.getInstances({
          tenantId: tid,
          status,
          managementType,
          solutionId,
          page: fetchAll ? 0 : pageIndex,
          size: fetchAll ? 5000 : (paginationSize ?? 10),
          search: this.searchText
        })
      );
      forkJoin(requests)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
          results => {
            // Merge all tenant data
            this.showSpinner = false;
            const mergedData = results.flatMap(res => res.data || []);
            this.allFetchedInstances = mergedData;
            this.instanceData = { ...results[0], data: mergedData };
            this.totalRecords = mergedData.length;
            this.updatePaginatedData();
            this.loadData(this.allFetchedInstances);
          },
        );
      return;
    }
     const instanceObj = {
      tenantId: Array.isArray(tenantId) ? tenantId.join(',') : tenantId,
      status,
      managementType,
      solutionId,
      page: fetchAll ? 0 : pageIndex,
      size: fetchAll ? 5000 : (paginationSize ?? 10),
      search: this.searchText
    };
     this.instanceService.getInstances(instanceObj)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        data => {
          if (data) {
            this.showSpinner = false;
            this.totalRecords = data?.total ?? 0;
            this.allFetchedInstances = data.data || [];
            this.instanceData = { ...data, data: this.allFetchedInstances };
            this.totalRecords = this.allFetchedInstances.length;
            this.updatePaginatedData();
            const terminatedInstances = data.data.filter(instance =>instance.status === DeployStatus.TERMINATED || instance.status === DeployStatus.TERMINATION_FAILED);
            this.instanceData.data = data.data.filter(instance => instance.status !== DeployStatus.TERMINATED && instance.status !== DeployStatus.TERMINATION_FAILED);
            this.instanceData.data = [...this.instanceData.data, ...terminatedInstances];
            const bookmarkedInstances = JSON.parse(localStorage.getItem('bookmarks') || '[]');
            this.instanceData.data.forEach(instance => {
              instance.bookmarked = bookmarkedInstances?.some(item => item.id === instance.id);
            });
            this.loadData(this.allFetchedInstances);
          }
        },
       
      );
  }

  updatePaginatedData(): void {
    const startIndex = this.pageIndex * this.paginationSize;
    const endIndex = startIndex + this.paginationSize;
    const sourceData = this.instanceData.data || [];
    this.dataSource.data = sourceData.slice(startIndex, endIndex);
    this.totalRecords = sourceData.length;
  }

  /**
   * Loads data to be displayed in table along with the default sorting based on datecreated field.
   */
  loadData(data: any[]): void {
    this.breadcrumbs();
    if (!data || data.length === 0) {
      this.dataSource = new MatTableDataSource([]);
      this.showPagination = false;
      return;
    }
    const sortedData = [...data].sort(
      (a, b) => new Date(b?.dateCreated).getTime() - new Date(a?.dateCreated).getTime()
    );
    this.dataSource = new MatTableDataSource(sortedData);
    this.dataSource.sort = this.sort;
    this.showPagination = this.totalRecords > this.paginationSize;
  }

  /**
   * Breadcrumbs of views instance page
   */
  breadcrumbs(): void {
    this.breadcrumbItems = [];
      this.breadcrumbItems.push({icon: 'home'}, {label: this.dashboardLink.label, url:[]}, {label: this.instanceLink.label,
        url: this.instanceLink.url});
  }

  /**
   * Sort the instance
   * @param event - the specific event
   */
  sortData(event): void {
    this.sortInfo = event;
    this.showPagination = false;
    setTimeout(() => {
      this.showPagination = true;
    }, 10);
    this.pageIndex = 0;
    this.instanceData.data = this.sortInstanceData(event);
    this.dataSource = new MatTableDataSource(this.instanceData.data?.slice(this.pageIndex, this.paginationSize));
  }

  /**
   * Sort the instance
   * @param event - sord data based on event
   * @returns - the data in ascending or descending order
   */
  sortInstanceData(event): any {
    return this.instanceData.data?.sort((a, b) => {
      let comparatorResult = 0;
      switch (event.active) {
        case 'name':
          comparatorResult = a?.name && b?.name ? a?.name?.localeCompare(b?.name) : !a?.name ? -1 : 1;
          break;
        case 'tenantId':
          comparatorResult = a?.tenantId && b?.tenantId ? a?.tenantId?.localeCompare(b?.tenantId) : !a?.tenantId ? -1 : 1;
          break;
        case 'dateCreated':
          comparatorResult = a?.dateCreated && b?.dateCreated ? new Date(a?.dateCreated)?.getTime() - new Date(b?.dateCreated)?.getTime()
            : !a?.dateCreated ? -1 : 1;
          break;
        case 'regionPair':
          comparatorResult = a?.region && b?.region ? a?.region?.localeCompare(b?.region)
            : !a?.region ? -1 : 1;
          break;
        case 'dataZone':
          comparatorResult = a?.dataZone && b?.dataZone ? a?.dataZone?.localeCompare(b?.dataZone)
            : !a?.dataZone ? -1 : 1;
          break;
        case 'deploymentClass':
          comparatorResult = a?.deploymentClass && b?.deploymentClass ? a?.deploymentClass?.localeCompare(b?.deploymentClass)
            : !a?.deploymentClass ? -1 : 1;
          break;
        case 'randomSuffix':
          comparatorResult = a?.randomSuffix && b?.randomSuffix ? a?.randomSuffix?.localeCompare(b?.randomSuffix)
            : !a?.randomSuffix ? -1 : 1;
          break;
        case 'status':
          comparatorResult = a?.status && b?.status ? a?.status?.localeCompare(b?.status) : !a?.status ? -1 : 1;
          break;
        default:
          comparatorResult = a?.name && b?.name ? a?.name?.localeCompare(b?.name) : !a?.name ? -1 : 1;
          break;
      }
      return comparatorResult * (event.direction === 'asc' ? 1 : -1);
     });
  }

  /**
   * Pagination for the view instance
   * @param event - event of the pagination
   */
  pageChanged(event: any): void {
    this.pageIndex = event.pageIndex;
    this.paginationSize = event.pageSize;
    this.updatePaginatedData();
  }

  /**
   * Open instance endpoints page
   * @param data - the data endpoints
   */
  showEndpoints(data): void {
    this.sidenavService.openInRightSidenav(InstanceEndpointsComponent, {data});
  }

  /**
   * Navigate to External instance page
   * @param instance - the instance
   */
  openInstanceDetails(instance): void {
    this.router.navigate(['/', this.authService.principal.provider, 'instance', instance.id]);
  }

  /**
   * Close the menu
   * @param data - type of management
   */
  closeMenu(data): void {
    this.sidenavService.searchFilter.next(data);
    this.dataSource.data = [];
    this.filteredColumnsFunction(data);
  }

  /**
   * The default filter for the instance
   * @param option - the selected event
   */
  defaultFilterChanged(option): void {
    if (option?.key) {
      this.dropdownsList[1].selectedValue = option.key;
      this.selectedDefaultFilter = option;
      this.getInstancesData(this.tenantId,
        this.dropdownsList[1].selectedValue, this.managementType, this.searchSolutionText, this.pageIndex, this.paginationSize, true);
  
      this.dropdownsList[1].options.forEach(item => item.selected = false);
      if (option?.key) {
        this.dropdownsList[1].options.find(item => item.value === option.key).selected = true;
        this.status = option.key;
      }
    } else {
      this.dropdownsList[1].selectedValue = undefined;
      this.selectedDefaultFilter = option;
      this.getInstancesData(this.tenantId,
      this.dropdownsList[1].selectedValue, this.managementType, this.searchSolutionText, this.pageIndex, this.paginationSize, true);
      this.dropdownsList[1].options.forEach(item => item.selected = false);
      this.status = undefined;
    }
  }

  /**
   * Handles the filter for the instance
   */
  filterColumns(): void {
    if(this.tenantId) {
      this.dropdownsList[0].options.filter((item) => {
        item.selected = false;
        if (this.isMultipleTenant && this.tenantId.find(tenant=> tenant == item.value)) {
          item.selected = true;
        } else if (item.value === this.tenantId) {
          item.selected = true;
        }
      });
    } else {
      this.dropdownsList[0].options.filter((item) => {
        item.selected = false;
      });
    }
    if(this.status) {
      this.dropdownsList[1].options.filter((item) => {
        if (item.value === this.status) {
          item.selected = true;
        }
      });
    } else {
      this.dropdownsList[1].options.filter((item) => {
        item.selected = false;
      });
    }
    if(this.managementType) {
      this.dropdownsList[2].options.filter((item) => {
        if (item.value === this.status) {
          item.selected = true;
        }
      });
    } else {
      this.dropdownsList[2].options.filter((item) => {
        item.selected = false;
      });
    }
    const data = {
      name: translate('INSTANCE_STATUS.INSTANCE'),
      title: translate('INSTANCE_STATUS.FILTER_TITLE'),
      subtitle: translate('INSTANCE_STATUS.FILTER_SUBTITLE'),
      filteredColumns: this.dropdownsList,
      tenant: this.dropdownsList[0].options,
      status: this.dropdownsList[1].options,
      type: this.dropdownsList[2].options,
    };
    this.sidenavService.openInRightSidenav(ColumnFilterComponent, data)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(filteredColumns => {
          if (filteredColumns) {
            this.clearData = true;
            this.dataSource.data = [];
            this.filteredColumns = filteredColumns;
            if(filteredColumns[0] !== '') {
              this.tenantId = filteredColumns[0];
            }
            if(filteredColumns[1] !== ''){
              this.defaultFilterOptions.forEach(item => item.selected = false);
              this.defaultFilterOptions[0].selected = true;
              this.selectedDefaultFilter = this.defaultFilterOptions[0];
              this.status = filteredColumns[1];
            }
            if(filteredColumns[2] !== ''){
              this.defaultFilterOptions.forEach(item => item.selected = false);
              this.defaultFilterOptions[0].selected = true;
              this.selectedDefaultFilter = this.defaultFilterOptions[0];
              this.managementType = filteredColumns[2];
            }
            this.getInstancesData(this.tenantId, this.status, this.managementType, this.searchSolutionText, this.pageIndex, this.paginationSize, true);
        }
        });
  }

  /**
   * Reset the selected property of the filter items
   * @param dataDeselected - selected filter item
   */
  filteredColumnsFunction(dataDeselected): void {
    if(dataDeselected) {
      if(this.isMultipleTenant && this.tenantId && this.tenantId.indexOf(dataDeselected) >= 0) {
        let selectedItem = this.tenantId.indexOf(dataDeselected);
        if (selectedItem >= 0) {
          this.tenantId.splice(selectedItem, 1);
        }
         if (this.tenantId.length > 1) {
          this.isMultipleTenant = true;
        } else if (this.tenantId.length === 1) {
          this.tenantId = this.tenantId[0];
          this.isMultipleTenant = false;
        }else {
          this.tenantId = undefined;
          this.isMultipleTenant = false;
        }
      } else if(this.tenantId === dataDeselected) {
        this.tenantId = undefined;
        this.resetValue(this.dropdownsList[0]);
      } else if(this.status === dataDeselected) {
        this.status = undefined;
        this.resetValue(this.dropdownsList[1]);
      } else if(this.managementType === dataDeselected) {
        this.managementType = undefined;
        this.resetValue(this.dropdownsList[2]);
      }

      if ([this.tenantId, this.status, this.managementType].every(value => value === undefined)) {
        this.selectedDefaultFilter = this.defaultFilterOptions[0];
      }

    }
    this.getInstancesData(this.tenantId, this.status, this.managementType, this.searchSolutionText, this.pageIndex, this.paginationSize, true);
  }

  /**
   * Reset all the filter items
   */
  resetFilter(): void {
    this.dataSource.data = [];
    this.tenantId = undefined;
    this.status = undefined;
    this.managementType = undefined;
    this.defaultFilterOptions.forEach(item => item.selected = false);
    this.defaultFilterOptions[0].selected = true;
    this.selectedDefaultFilter = this.defaultFilterOptions[0];
    this.clearData = false;
    this.sidenavService.searchFilter.next('data');
    this.resetAllValues();
    this.getInstancesData(this.tenantId, this.status, this.managementType, this.searchSolutionText, this.pageIndex, this.paginationSize, true);
  }

  /**
   * Reset filter dropdown list
   * @param closedItem - the selected item to be reset
   */
  resetValue(closedItem): void {
    closedItem?.options?.forEach(element => {
      element.selected = false;
    });
  }

  /**
   * Reset all filter dropdowns
   */
  resetAllValues(): void {
    this.dropdownsList.forEach(element => {
     element?.options?.forEach(item => {
       item.selected = false;
     });
   });
  }

  /**
   * Search instance
   * @param event - the event
   */
   searchInstance(event?: any): void {
    if (event) {
      this.searchText = event.target.value.toLowerCase().trim();
      this.pageIndex = 0;
    }
    if (this.searchText) {
      const filtered = this.allFetchedInstances.filter(item => {
        const envName = item.name?.toLowerCase() || '';
        const tenant = item.tenantId?.toLowerCase() || '';
        return (
          envName.includes(this.searchText) || 
          tenant.includes(this.searchText)
        );
      });
      if (filtered.length === 0) {
        this.instanceData.data = [];
        this.instanceData.totalElements = 0;
      } else {
        this.instanceData.data = filtered;
        this.instanceData.totalElements = filtered.length;
      }
    } else {
      this.instanceData.data = [...this.allFetchedInstances];
      this.instanceData.totalElements = this.allFetchedInstances.length;
    }
    this.updatePaginatedData();
  }

  /**
   * Add / remove bookmark for the selected instance
   * @param data - the selected instance
   */
  addRemoveBookmark(data): void {
    setTimeout(() => {
      if (!data.bookmarked) {
        const instance: any = {};
        instance.id = data.id;
        instance.name = data.name;
        instance.dateUpdated = data.dateUpdated;
        instance.type = 'instance';
        if (localStorage.getItem('bookmarks')) {
          let bookmarkList = JSON.parse(localStorage.getItem('bookmarks'));
          bookmarkList = [...bookmarkList, instance];
          localStorage.setItem('bookmarks', JSON.stringify(bookmarkList));
        } else {
          localStorage.setItem('bookmarks', JSON.stringify([instance]));
        }
        this.instanceData.data.find(item => item.id === data.id).bookmarked = true;
        this.dataSource.filteredData.find(item => item.id === data.id).bookmarked = true;
        setTimeout(() => {
          const message = translate('INSTANCE_LIST.BOOKMARKED');
            const title = translate('INSTANCE_LIST.BOOKMARK');
            this.customToastrService.successMessage(message, title);
        }, 500);
      } else {
        let bookmarkList = JSON.parse(localStorage.getItem('bookmarks'));
        bookmarkList = bookmarkList.filter(item => item.id !== data.id);
        localStorage.setItem('bookmarks', JSON.stringify(bookmarkList));
        this.instanceData.data.find(item => item.id === data.id).bookmarked = false;
        this.dataSource.filteredData.find(item => item.id === data.id).bookmarked = false;
        setTimeout(() => {
          const message = translate('INSTANCE_LIST.BOOKMARK_DELETED');
            const title = translate('INSTANCE_LIST.BOOKMARK');
            this.customToastrService.successMessage(message, title);
        }, 500);
      }
    }, 50);
  }
}
